import { defineMessages, useIntl } from 'react-intl';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { TenantEnum } from '@jsmdg/tenancy';
import { Breakpoint, LayoutVariant, useBreakpoint } from '@jsmdg/yoshi';
import { type FragmentContext } from '../../shared/types/fragmentContext';
import jsDesktopImage from '../assets/erlebnis-garantie-js.jpg';
import mdDesktopImage from '../assets/erlebnis-garantie-md.jpg';

const messages = defineMessages({
    headlineJS: {
        defaultMessage: 'Immer das richtige Geschenk:',
    },
    headlineMD: {
        defaultMessage: 'Immer das passende Geschenk:',
    },
    description: {
        defaultMessage: 'Große Auswahl, volle Flexibilität und maximale Sicherheit',
    },
    bulletsAccordionTitle: {
        defaultMessage: 'Große Auswahl, volle Flexibilität und maximale Sicherheit',
    },
    bulletServiceJS: {
        defaultMessage: 'Große Auswahl: Über 9.000 Erlebnisse.',
    },
    bulletServiceMD: {
        defaultMessage: 'Große Auswahl: Über 9.000 unvergessliche Erlebnisse.',
    },
    bulletFlexibility: {
        defaultMessage: 'Volle Flexibilität: Jeder Gutschein für alle Erlebnisse einlösbar.',
    },
    bulletSecurity: {
        defaultMessage: 'Maximale Sicherheit: {years} Jahre gültig & verlängerbar.',
    },
});

type BulletItem = {
    text: string;
};

type BaseConfig = {
    headline: string;
    bulletItems: BulletItem[];
    isMydays: boolean;
};

type DesktopConfig = BaseConfig & {
    layoutVariant: typeof LayoutVariant.WithImage;
    description: string;
    imageSrc: string;
    imageAlt: string;
    imageWidth: number;
    imageHeight: number;
};

type MobileConfig = BaseConfig & {
    layoutVariant: typeof LayoutVariant.Compact;
    collapsibleBullets: boolean;
    bulletsAccordionTitle: string;
};

type ExperienceGuaranteeConfig = DesktopConfig | MobileConfig;

const isAtOrCh = (tenant: TenantEnum): boolean =>
    tenant === TenantEnum.JS_AT || tenant === TenantEnum.MD_AT || tenant === TenantEnum.MD_CH;

export const useExperienceGuaranteeConfig = (): ExperienceGuaranteeConfig => {
    const intl = useIntl();
    const { isMydays, tenant } = useFragmentContext<FragmentContext>();
    const isDesktop = useBreakpoint(Breakpoint.SM);

    const bulletItems: BulletItem[] = [
        {
            text: intl.formatMessage(
                isMydays ? messages.bulletServiceMD : messages.bulletServiceJS,
            ),
        },
        { text: intl.formatMessage(messages.bulletFlexibility) },
        {
            text: intl.formatMessage(messages.bulletSecurity, {
                years: isAtOrCh(tenant) ? 10 : 3,
            }),
        },
    ];

    const headline = isMydays
        ? intl.formatMessage(messages.headlineMD)
        : intl.formatMessage(messages.headlineJS);

    const baseConfig: BaseConfig = {
        headline,
        bulletItems,
        isMydays,
    };

    if (isDesktop) {
        return {
            ...baseConfig,
            layoutVariant: LayoutVariant.WithImage,
            description: intl.formatMessage(messages.description),
            imageSrc: isMydays ? mdDesktopImage : jsDesktopImage,
            imageAlt: intl.formatMessage(messages.description),
            imageWidth: 300,
            imageHeight: 215,
        } as DesktopConfig;
    }

    return {
        ...baseConfig,
        layoutVariant: LayoutVariant.Compact,
        collapsibleBullets: true,
        bulletsAccordionTitle: intl.formatMessage(messages.bulletsAccordionTitle),
    } as MobileConfig;
};
